// Copyright (c) StrangeBee 2023

import React, { ReactElement, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocalMessage } from '@Helpers/locale';
import { THLayout } from '@Molecules/THLayout';
import { TitleSection } from '@Atoms/TitleSection/TitleSection';
import { THForm } from '@Molecules/THForm/THForm';
import { THInput } from '@Molecules/Inputs/THInput/THInput';
import { THSwitch } from '@Molecules/Inputs/THSwitch/THSwitch';
import THTable from '@Molecules/THTable/THTable';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { Button, Tag } from 'antd';
import client from '@Client';
import { useGenericQuery } from '@Queries/genericQueries';
import { libConfigDrawer } from '@Organisms/PlatformConfig/Provider/ProviderDrawer';
import { handleNotificationError, openMessage } from '@Helpers/notification';
import { StatusContext } from '@Context/StatusProvider';
import { classList } from '@Helpers/';
import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import { THNumberSelect } from '@Molecules/Inputs/THNumberSelect/THNumberSelect';
import { ColumnTitle } from '@Atoms/ColumnTitle/ColumnTitle';
import useValidateForm from '@Hooks/useValidateForm';
import RouteBuilder from '@Services/RouteBuilder';
import { RouteType } from '@Types';
import { PermissionsEnum, AuthProviderConfigurationOutput } from 'thehive-sdk';
import lodash from 'lodash';
import { ICONS } from '@Constants/iconsApp';

import './AuthenticationDetails.scss';

const nameKey = {
    session: {
        timeout: ['session', 'timeout'],
        warning: ['session', 'warning'],
    },
    key: {
        enabled: ['key', 'enabled'],
    },
    multifactor: { enabled: ['multifactor', 'enabled'] },
    basic: {
        enabled: ['basic', 'enabled'],
        realm: ['basic', 'realm'],
    },
    header: {
        enabled: ['header', 'enabled'],
        userHeader: ['header', 'userHeader'],
    },
    defaultUserDomain: ['defaultUserDomain'],
};

const prepareValue: Record<string, (value: any, defaultValue: any) => Record<string, any>> = {
    key: (value) => ({
        ...value,
        enabled: true,
    }),
    basic: (value) => ({
        ...value,
        enabled: true,
    }),
    header: (value) => ({
        ...value,
        enabled: true,
    }),
    session: ({ timeout, warning }, { session = {} }) => {
        return {
            timeout: timeout || session.timeout || null,
            warning: warning || session.warning || null,
        };
    },
    default: (st: any) => st,
};

const computeProvidersOutput: Record<string, any> = {
    key: ({ enabled, ...rest }: { enabled: boolean }) => rest, //eslint-disable-line
    basic: ({ enabled, ...rest }: { enabled: boolean }) => rest, //eslint-disable-line
    header: ({ enabled, ...rest }: { enabled: boolean }) => rest, //eslint-disable-line
    local: (st: any) => {
        const { resetAfter, maxAttempts, ...rest } = st;
        const policyConfig = !st?.passwordPolicy?.enabled ? { passwordPolicy: { enabled: false } } : rest;
        return { ...policyConfig, resetAfter, maxAttempts };
    },
    session: (st: any) => {
        return st;
    },
    default: (st: any) => st,
};

const orderOuputProvider = ['key', 'basic', 'header', 'session'];
const refProviderOrdering = ['local', 'ad', 'ldap', 'oauth2', 'sso'];

const AuthDetails = (): ReactElement => {
    const local = useLocalMessage();

    const { licenseCapabilities } = useContext(StatusContext);

    const authType = useMemo<string[]>(() => Object.keys(licenseCapabilities.auth || {}), [licenseCapabilities]);

    const { form, isValid, onFormValuesChange, fieldsTouched, setFieldsTouched } = useValidateForm({
        isValidInit: true,
    });

    const [defaultsConfig, setDefaultsConfig] = useState<Record<string, any>>({});

    const [enabledConfig, setEnabledConfig] = useState<string[]>([]);
    const [configStarted, setConfigStarted] = useState<Record<string, any>>({});
    const [configUpdated, setConfigUpdated] = useState<Record<string, any>>({});
    const [configUser, setConfigUser] = useState<Record<string, any>>({});

    const [providersTouched, setProvidersTouched] = useState(false);

    const [listProvider, setListProvider] = useState<Record<string, any>[]>([]);

    const [providerOrder, setProviderOrder] = useState<string[]>([]);
    const [providerEnableOrder, setProviderEnableOrder] = useState<string[]>([]);

    const [availableListProviders, setAvailableListProviders] = useState<string[]>([]);

    const configProviders: Record<string, any> = useMemo(
        () => ({
            local: { id: 'local', displayName: local('configPlateform.local.displayName'), type: ['local'] },
            directory: {
                id: 'directory',
                displayName: local('configPlateform.directory.displayName'),
                type: ['ad', 'ldap'],
            },
            oauth2: {
                id: 'oauth2',
                displayName: local('configPlateform.authentication.displayName'),
                type: ['oauth2'],
            },
            sso: {
                id: 'sso',
                displayName: local('configPlateform.sso.displayName'),
                type: ['sso'],
            },
        }),
        [local],
    );

    const [subShowConfigProvidersDisplay, setShowConfigProvidersDisplay] = useState({
        basic: false,
        header: false,
    });

    const { data: authProviderConfig, refetch } = useGenericQuery({
        fetchCallback: () => client.AuthProviderController.getConfig(),
        key: ['get-auth-provider-config'],
    });

    const updateProvider = (type: string) => (state: any) => {
        const realType = state.UIType;
        const enabledTmp = state.UIenabled;
        setProvidersTouched(true);

        const cleanTypes = configProviders?.[type]?.type || [];

        delete state.UIenabled;
        delete state.UIType;

        setEnabledConfig((st) => [...st.filter((v) => !cleanTypes.includes(v))]);

        if (enabledTmp) {
            setEnabledConfig((st) => [...st, realType]);
            if (!providerEnableOrder.includes(type)) {
                setProviderEnableOrder([...providerEnableOrder, type]);
            }
        } else {
            const cleanOrder = providerOrder.filter((name) => name !== type);
            setProviderOrder([...cleanOrder, type]);
            setProviderEnableOrder(cleanOrder);
        }

        setConfigUpdated((st) => {
            if (!enabledTmp) {
                cleanTypes.forEach((v: string) => {
                    delete st?.[v];
                });
                return { ...st };
            }

            if (realType === 'oauth2') {
                setConfigUser({ ...state.user });
                delete state.user;
            }
            return {
                ...st,
                [realType]: { ...state },
            };
        });

        onFormValuesChange();
        setFieldsTouched(true);
    };

    const onChangeSwitch = (field: string) => (value: boolean) => {
        setEnabledConfig((st) => st.filter((v) => v !== field));
        if (value) {
            setEnabledConfig((st) => [...st, field]);
        }
        setShowConfigProvidersDisplay((st) => ({ ...st, [field]: value }));
    };

    const getValueProvider = useCallback(
        (type = ''): Record<string, any> => {
            let complement = {};
            if (type === 'oauth2') {
                complement = { user: configUser };
            }
            const data = { ...(configUpdated[type] || configStarted[type] || {}), ...complement };
            return lodash.cloneDeep(data);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [configUpdated, configStarted, configUser],
    );

    const changeOrder = (direction: number, _id: string) => {
        const tmpProviderOrder = [...providerOrder];
        const currentIndex = tmpProviderOrder.findIndex((type) => type == _id);
        const newIndex = currentIndex + 1 * direction;

        tmpProviderOrder.splice(currentIndex, 1);
        tmpProviderOrder.splice(newIndex, 0, _id);
        setFieldsTouched(true);
        setProviderOrder(tmpProviderOrder);
        setProvidersTouched(true);
    };

    const treatData = useCallback((authProviderConfig: AuthProviderConfigurationOutput, authType: string[]) => {
        const { multifactor, user, defaults, providers, defaultUserDomain } = lodash.cloneDeep(authProviderConfig);
        const formatedProviders = providers.reduce((acc, { name, ...props }): Record<string, any> => {
            const treatValue = (prepareValue[name] || prepareValue.default)(props, defaults);
            return {
                ...acc,
                [name]: { ...treatValue },
            };
        }, {});

        /** */
        setDefaultsConfig(defaults);
        setConfigStarted({ ...formatedProviders, multifactor, defaultUserDomain });
        setConfigUser(user);
        setAvailableListProviders(authType);
    }, []);

    const columns = useMemo(
        () => [
            {
                title: <ColumnTitle>{local('configPlateform.column.order')}</ColumnTitle>,
                key: 'order',
                className: 'auth-order custom-table__cell',
                render: (data: any) => {
                    const arrType = configProviders?.[data.id]?.type || [];
                    const type = arrType.find((type: string) => enabledConfig.includes(type));

                    const enabled = !!type;
                    const index = providerOrder.findIndex((name) => name === data.id);

                    const isFirst = index == 0;
                    const isLast = index === providerEnableOrder.length - 1;

                    return (
                        <div className={classList('btn-order', { disabled: !enabled })}>
                            <Button
                                type="link"
                                disabled={!enabled || isFirst}
                                className="btn-order__item up"
                                onClick={() => {
                                    changeOrder(-1, data.id);
                                }}
                            >
                                &#9650;
                            </Button>
                            <Button
                                type="link"
                                disabled={!enabled || isLast}
                                className="btn-order__item down"
                                onClick={() => {
                                    changeOrder(1, data.id);
                                }}
                            >
                                &#9660;
                            </Button>
                        </div>
                    );
                },
            },
            {
                title: <ColumnTitle>{local('configPlateform.column.type')}</ColumnTitle>,
                key: 'type',
                className: 'custom-table__cell',
                render: (data: any): ReactElement => {
                    const { id, displayName } = data;
                    const typeRef = id.toLowerCase();

                    const ProviderDrawer = libConfigDrawer[typeRef] || libConfigDrawer.default;

                    const disabledProvider =
                        typeRef === 'directory'
                            ? !availableListProviders?.includes('ad') && !availableListProviders?.includes('ldap')
                            : !availableListProviders.includes(typeRef);

                    return (
                        <ProviderDrawer
                            type={typeRef}
                            title={displayName}
                            enableList={enabledConfig}
                            getData={getValueProvider}
                            defaultConfig={defaultsConfig}
                            onSubmit={updateProvider(typeRef)}
                            availableProviders={availableListProviders}
                            triggerElem={
                                <THButton type="link" disabled={disabledProvider}>
                                    {displayName}
                                </THButton>
                            }
                            deps={[configStarted, configUpdated]}
                        />
                    );
                },
            },
            {
                title: <ColumnTitle>{local('configPlateform.column.status')}</ColumnTitle>,
                key: 'status',
                className: 'auth-status custom-table__cell',
                render: (data): ReactElement => {
                    const arrType = configProviders?.[data.id]?.type || [];
                    const type = arrType.find((type: string) => enabledConfig.includes(type));
                    return !!type ? (
                        <Tag color="green">{local('configPlateform.general.enable')}</Tag>
                    ) : (
                        <Tag>{local('configPlateform.general.disable')}</Tag>
                    );
                },
            },
        ],
        [availableListProviders, changeOrder, enabledConfig, local, providerOrder, getValueProvider], //eslint-disable-line
    );

    const onFinishHandler = (values: any) => {
        const result = { ...configStarted, ...values, ...configUpdated };

        const providerOrderTreat = providerOrder.reduce<string[]>((acc, current) => {
            const type = current === 'directory' ? ['ad', 'ldap'] : [current];
            return [...acc, ...type];
        }, []);

        const providersOutPut = [...orderOuputProvider, ...providerOrderTreat].reduce<any[]>((acc, type: string) => {
            if (!enabledConfig.includes(type)) {
                return acc;
            }

            const configTreated = (computeProvidersOutput[type] || computeProvidersOutput.default)(result[type] || {});

            return [...acc, { name: type, ...configTreated }];
        }, []);

        const updated = {
            ...authProviderConfig,
            user: configUser,
            multifactor: values.multifactor,
            providers: providersOutPut,
            defaultUserDomain: values?.defaultUserDomain,
        };

        client.AuthProviderController.edit(updated)
            .then(() => {
                openMessage('success', local('configPlateform.general.success'));
                if (updated) {
                    treatData(updated as AuthProviderConfigurationOutput, authType);
                }
                setProvidersTouched(false);
                setConfigUpdated({});
                setFieldsTouched(false);
                refetch();
            })
            .catch(handleNotificationError);
    };

    const onResetForm = () => {
        if (authProviderConfig) {
            treatData(authProviderConfig, authType);
        }
        setProvidersTouched(false);
        setConfigUpdated({});
        setFieldsTouched(false);
        form.resetFields();
    };

    const initProviderOrder = () => {
        let currentOrder = Object.keys(configStarted).reduce<string[]>((acc, name) => {
            if (refProviderOrdering.includes(name)) {
                const added = ['ad', 'ldap'].includes(name) ? 'directory' : name;
                return [...acc, added];
            }
            return acc;
        }, []);

        /** */
        setProviderEnableOrder([...currentOrder]);

        refProviderOrdering.forEach((name) => {
            const type = ['ad', 'ldap'].includes(name) ? 'directory' : name;
            if (!currentOrder.includes(type)) {
                currentOrder = [...currentOrder, type];
            }
        });

        setProviderOrder([...currentOrder]);
    };

    useEffect(() => {
        if (authProviderConfig) {
            treatData(authProviderConfig, authType);
        }
    }, [authProviderConfig, authType, treatData]);

    useEffect(() => {
        const list = providerOrder.map((type) => configProviders?.[type]);
        setListProvider(list);
    }, [providerOrder, treatData]); // eslint-disable-line

    useEffect(() => {
        setShowConfigProvidersDisplay({
            basic: enabledConfig.includes('basic'),
            header: enabledConfig.includes('header'),
        });
    }, [enabledConfig]);

    useEffect(() => {
        setEnabledConfig(Object.keys(configStarted));
        initProviderOrder();
        form.resetFields();
        // eslint-disable-next-line
    }, [configStarted]);

    return (
        <THLayout>
            <THLayout.Content className="auth-layout">
                <THForm
                    form={form}
                    autoComplete="off"
                    onFinish={onFinishHandler}
                    initialValues={configStarted}
                    onValuesChange={() => {
                        setFieldsTouched(true);
                        onFormValuesChange();
                    }}
                >
                    <THForm.Layout
                        footer={
                            <FormSubmitButton
                                warning
                                onCancel={onResetForm}
                                disabled={!isValid}
                                hidden={!fieldsTouched && !providersTouched}
                            />
                        }
                    >
                        <div className="auth-details__category auth-details__session">
                            <TitleSection>{local('configPlateform.authentication.session')}</TitleSection>
                            <div className="auth-details__section">
                                <THForm.Item
                                    name={nameKey.session.timeout}
                                    className="auth-input__duration input-half"
                                    label={local('configPlateform.authentication.timeout')}
                                    rules={[{ required: true, message: local('configPlateform.required.general') }]}
                                >
                                    <THNumberSelect
                                        options={['seconds', 'minutes', 'hours']}
                                        disabled={!availableListProviders.includes('session')}
                                    />
                                </THForm.Item>

                                <THForm.Item
                                    name={nameKey.session.warning}
                                    label={local('configPlateform.authentication.warning')}
                                    className="auth-input__duration input-half"
                                    rules={[{ required: true, message: local('configPlateform.required.general') }]}
                                >
                                    <THNumberSelect
                                        options={['seconds', 'minutes', 'hours']}
                                        disabled={!availableListProviders.includes('session')}
                                    />
                                </THForm.Item>
                            </div>
                        </div>
                        <div className="auth-details__category auth-details__advanced">
                            <TitleSection>{local('configPlateform.general.advanced')}</TitleSection>
                            <div className="auth-details__section">
                                <THForm.Item
                                    name={nameKey.key.enabled}
                                    label={local('configPlateform.authentication.key.enabled')}
                                    valuePropName="checked"
                                >
                                    <THSwitch
                                        onChange={onChangeSwitch('key')}
                                        defaultChecked={configStarted?.key?.enabled}
                                        disabled={!availableListProviders.includes('key')}
                                    />
                                </THForm.Item>
                                <div className="form-auth__subinput  input-half">
                                    <THForm.Item
                                        name={nameKey.basic.enabled}
                                        label={local('configPlateform.authentication.basic.enabled')}
                                        valuePropName="checked"
                                    >
                                        <THSwitch
                                            onChange={onChangeSwitch('basic')}
                                            defaultChecked={configStarted?.basic?.enabled}
                                            disabled={!availableListProviders.includes('basic')}
                                        />
                                    </THForm.Item>
                                    <THForm.Item
                                        name={nameKey.basic.realm}
                                        className="form-auth__subinput-child"
                                        label={local('configPlateform.authentication.basic.realm')}
                                        hidden={!subShowConfigProvidersDisplay.basic}
                                        rules={[
                                            {
                                                required: subShowConfigProvidersDisplay.basic,
                                                message: local('configPlateform.required.general'),
                                            },
                                        ]}
                                    >
                                        <THInput disabled={!availableListProviders.includes('basic')} />
                                    </THForm.Item>
                                </div>
                                <div className="form-auth__subinput  input-half">
                                    <THForm.Item
                                        name={nameKey.header.enabled}
                                        label={local('configPlateform.authentication.header.enabled')}
                                        valuePropName="checked"
                                    >
                                        <THSwitch
                                            onChange={onChangeSwitch('header')}
                                            defaultChecked={configStarted?.header?.enabled}
                                            disabled={!availableListProviders.includes('header')}
                                        />
                                    </THForm.Item>
                                    <THForm.Item
                                        name={nameKey.header.userHeader}
                                        label={local('configPlateform.authentication.header.userHeader')}
                                        className="form-auth__subinput-child"
                                        hidden={!subShowConfigProvidersDisplay.header}
                                        rules={[
                                            {
                                                required: subShowConfigProvidersDisplay.header,
                                                message: local('configPlateform.required.general'),
                                            },
                                        ]}
                                    >
                                        <THInput disabled={!availableListProviders.includes('header')} />
                                    </THForm.Item>
                                </div>
                                <THForm.Item
                                    name={nameKey.multifactor.enabled}
                                    label={local('configPlateform.authentication.multifactor.enabled')}
                                    valuePropName="checked"
                                >
                                    <THSwitch />
                                </THForm.Item>
                                <THForm.Item
                                    name={nameKey.defaultUserDomain}
                                    label={local('configPlateform.authentication.defaultUserDomain')}
                                    className="input-half"
                                >
                                    <THInput addonBefore="@" />
                                </THForm.Item>
                            </div>
                        </div>
                        <div className="auth-details__category auth-details__providers">
                            <TitleSection>{local('configPlateform.authentication.providers')}</TitleSection>
                            <div className="auth-providers__wrapper">
                                <THTable dataSource={listProvider} rowKey="id" columns={columns} />
                            </div>
                        </div>
                    </THForm.Layout>
                </THForm>
            </THLayout.Content>
        </THLayout>
    );
};
const path = RouteBuilder.compute('administration.authentication');
export const Route: RouteType = {
    path,
    menu: {
        label: 'mainLayout.adminMenu.authentication',
        icon: <ICONS.LockOutlined />,
    },
    breadcrumb: { label: 'breadcrumb.global.authentication' },
    content: <AuthDetails />,
    permissionNeeded: [PermissionsEnum.MANAGE_CONFIG],
    space: ['admin'],
    exact: true,
};
